import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import HandymanIcon from "@mui/icons-material/Handyman";
import PeopleIcon from "@mui/icons-material/People";
import { AccountSidebarPreview } from "./AccountSidebarPreview";
import { PoweredByFooter } from "./PoweredByFooter";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../generated/api-client";

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(6)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(2, 1),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth + 16 : `calc(${theme.spacing(5)} + 1px)`,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "& .MuiDrawer-paper": {
    borderRadius: 8,
    border: `0px solid ${theme.palette.divider}`,
    margin: open ? 8 : 4,
    height: open
      ? "calc(100% - 16px) !important"
      : "calc(100% - 8px) !important",
    color: "#FFFFFF",
    ...(open && {
      ...openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
    }),
  },
  [theme.breakpoints.up("sm")]: {
    width: open ? drawerWidth + 16 : `calc(${theme.spacing(6)} + 8px)`,
  },
}));

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const [open, setOpen] = React.useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const isAdmin =
    user?.role === UserRole.Admin || user?.role === UserRole.Mantainer;

  const menuItems = [
    { text: "Strumenti", path: "/", icon: <HandymanIcon /> },
    ...(isAdmin
      ? [{ text: "Utenti", path: "/users", icon: <PeopleIcon /> }]
      : []),
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const handleToggle = () => {
    setOpen(!open);
  };

  const buildList = (expanded: boolean, isMobileDrawer: boolean = false) => (
    <List sx={{ marginLeft: 0.6, marginRight: 0.6, flexShrink: 0, p: 0 }}>
      {menuItems.map((item) => (
        <ListItem key={item.text} disablePadding sx={{ display: "block", mb: 0.5 }}>
          <ListItemButton
            component={Link}
            to={item.path}
            selected={isActive(item.path)}
            onClick={() => {
              if (isMobileDrawer) setMobileSidebarOpen(false);
            }}
            sx={{
              py: 1,
              px: 1,
              justifyContent: expanded ? "initial" : "center",
              borderRadius: 3,
              "&.Mui-selected": {
                backgroundColor: "rgba(224, 224, 224, 0.95)",
                "&:hover": { backgroundColor: "rgba(224, 224, 224, 0.95)" },
                "& .MuiListItemIcon-root": { color: "rgba(0, 0, 0, 0.87)" },
                "& .MuiListItemText-primary": { color: "rgba(0, 0, 0, 0.87)" },
              },
              "&:hover": {
                backgroundColor: isActive(item.path)
                  ? "rgba(224, 224, 224, 0.95)"
                  : "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: "center",
                mr: expanded ? 1 : 0,
                color: isActive(item.path)
                  ? "rgba(0, 0, 0, 0.87)"
                  : "rgba(255, 255, 255, 0.7)",
                "& svg": { fontSize: "1.5rem" },
              }}
            >
              {item.icon}
            </ListItemIcon>
            {expanded && (
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    color: isActive(item.path)
                      ? "rgba(0, 0, 0, 0.87)"
                      : "rgba(255, 255, 255, 0.7)",
                  },
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  const renderSidebarContent = (isMobileDrawer: boolean) => {
    const expanded = isMobileDrawer ? true : open;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <DrawerHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: expanded ? "space-between" : "center",
            }}
          >
            {expanded ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, minWidth: 0 }}>
                  <Box
                    component="img"
                    src="/assets/long_light.png"
                    alt="Logo"
                    sx={{ height: 32, maxWidth: 140, objectFit: "contain" }}
                  />
                </Box>
                <IconButton
                  onClick={() => {
                    if (isMobileDrawer) setMobileSidebarOpen(false);
                    else handleToggle();
                  }}
                  sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#FFFFFF" } }}
                >
                  {theme.direction === "rtl" ? (
                    <ChevronRightIcon />
                  ) : (
                    <ChevronLeftIcon />
                  )}
                </IconButton>
              </>
            ) : (
              <Box
                onClick={handleToggle}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.75rem" }}
                >
                  FH
                </Typography>
              </Box>
            )}
          </Box>
        </DrawerHeader>

        {buildList(expanded, isMobileDrawer)}

        <Box sx={{ flexGrow: 1 }} />

        <Box onClick={(e) => e.stopPropagation()}>
          <AccountSidebarPreview mini={!expanded} open={expanded} />
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        overflow: "hidden",
      }}
      color="background.default"
    >
      {!isMobile && (
        <Drawer variant="permanent" open={open} color="background.default">
          {renderSidebarContent(false)}
        </Drawer>
      )}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              color: "#FFFFFF",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              boxShadow: "none",
            },
          }}
        >
          {renderSidebarContent(true)}
        </MuiDrawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#FFFFFF",
          height: "100vh",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          borderTopLeftRadius: { xs: 0, md: "2rem" },
          borderBottomLeftRadius: { xs: 0, md: "2rem" },
        }}
      >
        {isMobile && !mobileSidebarOpen && (
          <IconButton
            onClick={() => setMobileSidebarOpen(true)}
            sx={{
              position: "fixed",
              top: 8,
              left: 8,
              zIndex: (t) => t.zIndex.drawer + 1,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {children}
        <Box sx={{ mt: "auto", flexShrink: 0 }}>
          <PoweredByFooter variant="dark" />
        </Box>
      </Box>
    </Box>
  );
};
